import React, { useEffect, useMemo, useState } from "react";
import ActivityLoader from "../Common/ActivityLoader";
import ajaxSchool from "../../util/remote/ajaxSchool";
import { useStudent } from "../../DataStore/FeesStudentContext";
import Notification from "../Common/Notification";
import AlertNotification from "../Common/AlertNotification";
import { useTheme } from "../../DataStore/ThemeContext";

const nf = (n) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const BillingPreviewList = ({ studentIds }) => {
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState();
  const { selectedIds } = useStudent();
  const [info, setInfo] = useState("");
  const { setSystemFunction } = useTheme();

  const selection = useMemo(
    () => (Array.isArray(studentIds) && studentIds.length ? studentIds : selectedIds),
    [studentIds, selectedIds]
  );

  useEffect(() => {
    getStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSystemFunction(() => async () => await handleConfirm());
    return () => setSystemFunction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, students]);

  const getStudents = async () => {
    if (!Array.isArray(selection) || selection.length === 0) return;

    setLoading(true);
    const server_response = await ajaxSchool.fetchBillingPreview(selection);
    setLoading(false);

    if (server_response.status === "OK") {
      setStudents(server_response.details);
    } else {
      setInfo(
        <AlertNotification
          type="danger"
          message={server_response.message}
          closable={false}
          iconClass="fa fa-info-circle"
        />
      );
      setStudents("404");
    }
  };

  const handleConfirm = async () => {
    if (!Array.isArray(students) || students.length === 0) {
      setInfo(
        <Notification
          type="warning"
          message="No students found to bill. Please try again later."
          closable={false}
        />
      );
      return;
    }

    setInfo("");
    setLoading(true);
    const server_response = await ajaxSchool.billSelection(selection);
    setLoading(false);

    if (server_response.status === "OK") {
      setInfo(<Notification type="success" message={server_response.message} closable={false} />);
    } else {
      setInfo(
        <AlertNotification
          type="warning"
          message={server_response.message}
          closable={false}
          iconClass="fa fa-info-circle"
        />
      );
    }
  };

  if ((!selectedIds || selectedIds.length === 0) && (!studentIds || studentIds.length === 0)) {
    return (
      <Notification
        type="danger"
        message="No student selection found. Please make selection then try again."
        closable={false}
      />
    );
  }

  return (
    <div>
      <style>{css}</style>

      {info}

      <div className="yx-bpList">
        {Array.isArray(students) &&
          students.map((s) => {
            const ok = !!s.apply_billing;

            return (
              <div key={s.id} className={`yx-bpItem ${ok ? "is-ok" : "is-bad"}`}>
                <div className="yx-bpTop">
                  <div style={{ minWidth: 0 }}>
                    <div className="yx-bpName" title={s.full_name}>
                      {s.full_name}
                    </div>
                    <div className="yx-bpSub">{s.reg_no}</div>
                  </div>

                  <span className={`yx-bpStatus ${ok ? "is-ok" : "is-bad"}`}>
                    <i className={`fa ${ok ? "fa-circle-check" : "fa-circle-xmark"}`} />
                    {ok ? "Billing can apply" : "Billing can’t apply"}
                  </span>
                </div>

                <div className="yx-bpMid">
                  <div className="yx-bpChip">
                    <span className="yx-bpChipLabel">Fees group total</span>
                    <span className="yx-bpChipValue">UGX {nf(s.presumed_bill)}</span>
                  </div>

                  <div className={`yx-bpChip ${ok ? "is-accent" : ""}`}>
                    <span className="yx-bpChipLabel">Bill to Apply</span>
                    <span className="yx-bpChipValue">UGX {nf(s.suggested_bill)}</span>
                  </div>
                </div>

                <div className="yx-bpComment" title={s.comment || ""}>
                  {s.comment || "—"}
                </div>
              </div>
            );
          })}

        {!students && <ActivityLoader />}

        {students === "404" ? null : Array.isArray(students) && students.length === 0 ? (
          <div className="text-center yx-muted py-4">No preview data found.</div>
        ) : null}
      </div>
    </div>
  );
};

const css = `

  .yx-bpItem{
    background: var(--yx-surface, #fff);
    border: 1px solid var(--yx-border-soft, rgba(15,23,42,.07));
    border-radius: var(--yx-card-radius, 18px);
    box-shadow: var(--yx-card-shadow, 0 1px 2px rgba(16,24,40,.04));
    padding: 14px 16px 12px;
    position: relative;
    overflow: hidden;
  }

  /* Thin accent rail (cleaner than full background fills) */
  .yx-bpItem::before{
    content:"";
    position:absolute;
    left:0; top:0; bottom:0;
    width: 4px;
    background: rgba(79,70,229,.65);
  }
  .yx-bpItem.is-bad::before{
    background: rgba(225,29,72,.65);
  }

  .yx-bpTop{
    display:flex;
    align-items:flex-start;
    justify-content: space-between;
    gap: 12px;
    min-width: 0;
  }

  .yx-bpName{
    font-weight: 950;
    font-size: 16px;
    color: rgba(15,23,42,.92);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 520px;
  }

  .yx-bpSub{
    margin-top: 4px;
    font-size: 12.5px;
    font-weight: 800;
    color: rgba(100,116,139,.92);
  }

  .yx-mono{
    font-variant-numeric: tabular-nums;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  }

  .yx-bpStatus{
    display:inline-flex;
    align-items:center;
    gap: 8px;
    height: 32px;
    padding: 0 12px;
    border-radius: 999px;
    font-weight: 900;
    font-size: 12.5px;
    white-space: nowrap;

    border: 1px solid rgba(79,70,229,.18);
    background: rgba(79,70,229,.08);
    color: var(--yx-accent, #4F46E5);
  }
  .yx-bpStatus i{ font-size: 14px; }

  .yx-bpStatus.is-bad{
    border-color: rgba(225,29,72,.18);
    background: rgba(225,29,72,.08);
    color: #E11D48;
  }

  .yx-bpMid{
    display:flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 12px;
  }

  .yx-bpChip{
    flex: 1 1 260px;
    min-width: 220px;
    border-radius: 14px;
    border: 1px solid rgba(15,23,42,.08);
    background: rgba(15,23,42,.03);
    padding: 10px 12px;
    display:flex;
    align-items:center;
    justify-content: space-between;
    gap: 10px;
  }

  .yx-bpChip.is-accent{
    border-color: rgba(79,70,229,.16);
    background: rgba(79,70,229,.06);
  }

  .yx-bpChipLabel{
    font-size: 12px;
    font-weight: 900;
    color: rgba(100,116,139,.92);
    text-transform: uppercase;
    white-space: nowrap;
  }

  .yx-bpChipValue{
    font-size: 13.5px;
    font-weight: 950;
    color: rgba(15,23,42,.92);
    white-space: nowrap;
  }

  .yx-bpComment{
    margin-top: 10px;
    font-size: 13px;
    font-weight: 800;
    color: rgba(71,85,105,.90);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 576px){
    .yx-bpStatus{ height: 30px; padding: 0 10px; font-size: 12px; }
    .yx-bpChip{ min-width: 100%; }
  }
`;

export default BillingPreviewList;
